import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate }                             from 'react-router-dom';
import { useTranslation }                          from 'react-i18next';
import Layout                                      from '../components/Layout';
import SetupChecklist                              from '../components/SetupChecklist';
import { useAuth }                                 from '../context/AuthContext';
import { stockService }                            from '../services/stockService';
import { stockTransferService }                    from '../services/stockTransferService';
import { damageNoteService }                       from '../services/damageNoteService';
import { stockReconciliationService }              from '../services/stockReconciliationService';
import { grnService }                              from '../services/grnService';
import { purchaseOrderService, PurchaseOrderDTO }  from '../services/purchaseOrderService';
import { salesInvoiceService, SalesInvoiceListDTO } from '../services/salesInvoiceService';
import warehouseService                            from '../services/warehouseService';
import { productService }                          from '../services/productService';
import {
  StockLevel,
  StockTransferStatsDTO,
  DamageNoteStatsDTO,
  StockReconciliationStatsDTO,
}                                                  from '../types';
import {
  Package, AlertTriangle, Warehouse, Clock, PackageCheck,
  ArrowLeftRight, PackageX, ScanLine, ShoppingCart, Receipt, Tag
} from 'lucide-react';
import './DashboardPage.css';

// ─── Local interfaces ─────────────────────────────────────────────────────────

interface GRNStatsDTO {
  totalGRN:            number;
  pendingApproval:     number;
  totalItemsReceived:  number;
  totalDefectiveItems: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt         = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtCurrency = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (iso: string): string => {
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const extractList = <T,>(d: unknown): T[] => {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>;
    if (Array.isArray(obj['data'])) return obj['data'] as T[];
  }
  return [];
};

const extractTotal = (d: unknown): number => {
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>;
    if (obj['pagination'] && typeof obj['pagination'] === 'object') {
      const p = obj['pagination'] as Record<string, unknown>;
      if (typeof p['total'] === 'number') return p['total'];
    }
  }
  if (Array.isArray(d)) return d.length;
  return 0;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StatCardProps {
  icon:      React.ReactNode;
  iconClass: string;
  value:     number | null;
  label:     string;
  onClick?:  () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconClass, value, label, onClick }) => (
  <div
    className={`db-stat-card${onClick ? ' db-stat-card--link' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
  >
    <div className={`db-stat-icon ${iconClass}`}>{icon}</div>
    <div className="db-stat-body">
      {value === null
        ? <div className="db-skeleton db-skeleton--val" />
        : <div className="db-stat-value">{fmt.format(value)}</div>
      }
      <div className="db-stat-label">{label}</div>
    </div>
  </div>
);

const POStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    Draft:             'db-badge db-badge-draft',
    Submitted:         'db-badge db-badge-info',
    Confirmed:         'db-badge db-badge-confirmed',
    PartiallyReceived: 'db-badge db-badge-confirmed',
    Received:          'db-badge db-badge-received',
    Closed:            'db-badge db-badge-closed',
    Cancelled:         'db-badge db-badge-cancelled',
  };
  return <span className={map[status] ?? 'db-badge db-badge-draft'}>{status}</span>;
};

const InvoiceStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    Draft:         'db-badge db-badge-draft',
    Issued:        'db-badge db-badge-info',
    PartiallyPaid: 'db-badge db-badge-confirmed',
    Paid:          'db-badge db-badge-received',
    Overdue:       'db-badge db-badge-cancelled',
    Voided:        'db-badge db-badge-closed',
    Cancelled:     'db-badge db-badge-cancelled',
  };
  return <span className={map[status] ?? 'db-badge db-badge-draft'}>{status}</span>;
};

interface WorkflowCardProps {
  title:   React.ReactNode;
  rows:    { label: string; value: number | null; accent?: boolean }[];
  onNavigate?: () => void;
  viewAllLabel?: string;
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({ title, rows, onNavigate, viewAllLabel }) => (
  <div className="db-workflow-card">
    <div className="db-workflow-header">
      <span className="db-workflow-title">{title}</span>
      {onNavigate && (
        <button className="db-view-all" onClick={onNavigate}>{viewAllLabel ?? 'View All →'}</button>
      )}
    </div>
    <div className="db-workflow-rows">
      {rows.map((r) => (
        <div key={r.label} className="db-workflow-row">
          <span className="db-workflow-row-label">{r.label}</span>
          {r.value === null
            ? <div className="db-skeleton db-skeleton--num" />
            : <span className={`db-workflow-row-value${r.accent ? ' db-workflow-row-value--accent' : ''}`}>
                {fmt.format(r.value)}
              </span>
          }
        </div>
      ))}
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t }    = useTranslation();
  const { user } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────
  const [totalProducts,   setTotalProducts]   = useState<number | null>(null);
  const [lowStockItems,   setLowStockItems]   = useState<StockLevel[]>([]);
  const [lowStockCount,   setLowStockCount]   = useState<number | null>(null);
  const [totalWarehouses, setTotalWarehouses] = useState<number | null>(null);
  const [pendingPOs,      setPendingPOs]      = useState<number | null>(null);
  const [grnStats,        setGrnStats]        = useState<GRNStatsDTO | null>(null);
  const [transferStats,   setTransferStats]   = useState<StockTransferStatsDTO | null>(null);
  const [damageStats,     setDamageStats]     = useState<DamageNoteStatsDTO | null>(null);
  const [reconcStats,     setReconcStats]     = useState<StockReconciliationStatsDTO | null>(null);
  const [recentPOs,       setRecentPOs]       = useState<PurchaseOrderDTO[]>([]);
  const [recentInvoices,  setRecentInvoices]  = useState<SalesInvoiceListDTO[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState<string | null>(null);
  const [lastUpdated,     setLastUpdated]     = useState<Date | null>(null);

  // ── Data loader ────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        productService.getProducts({ pageNumber: 1, pageSize: 1 }),               // 0
        stockService.getLowStockReport(),                                           // 1
        warehouseService.getWarehouses(1, 1),                                      // 2
        purchaseOrderService.getPendingApprovals({ pageNumber: 1, pageSize: 1 }), // 3
        grnService.getGRNStats(),                                                  // 4
        stockTransferService.getStats(),                                           // 5
        damageNoteService.getStats(),                                              // 6
        stockReconciliationService.getStats(),                                     // 7
        purchaseOrderService.getPurchaseOrders({ pageNumber: 1, pageSize: 5 }),   // 8
        salesInvoiceService.getSalesInvoices({ pageNumber: 1, pageSize: 5 }),     // 9
      ]);

      // 0 — total products
      if (results[0].status === 'fulfilled') {
        const d = (results[0].value.data as any)?.data;
        setTotalProducts(extractTotal(d));
      }

      // 1 — low stock
      if (results[1].status === 'fulfilled') {
        const d = (results[1].value.data as any)?.data;
        const arr = extractList<StockLevel>(d);
        setLowStockCount(arr.length);
        setLowStockItems(arr.slice(0, 8));
      }

      // 2 — total warehouses
      if (results[2].status === 'fulfilled') {
        const d = (results[2].value.data as any)?.data;
        setTotalWarehouses(extractTotal(d));
      }

      // 3 — my pending PO approvals
      if (results[3].status === 'fulfilled') {
        const d = (results[3].value.data as any)?.data;
        setPendingPOs(extractTotal(d));
      }

      // 4 — GRN stats
      if (results[4].status === 'fulfilled') {
        const d = (results[4].value.data as any)?.data;
        if (d) setGrnStats(d as GRNStatsDTO);
      }

      // 5 — transfer stats
      if (results[5].status === 'fulfilled') {
        const d = (results[5].value.data as any)?.data;
        if (d) setTransferStats(d as StockTransferStatsDTO);
      }

      // 6 — damage note stats
      if (results[6].status === 'fulfilled') {
        const d = (results[6].value.data as any)?.data;
        if (d) setDamageStats(d as DamageNoteStatsDTO);
      }

      // 7 — reconciliation stats
      if (results[7].status === 'fulfilled') {
        const d = (results[7].value.data as any)?.data;
        if (d) setReconcStats(d as StockReconciliationStatsDTO);
      }

      // 8 — recent POs
      if (results[8].status === 'fulfilled') {
        const d = (results[8].value.data as any)?.data;
        setRecentPOs(extractList<PurchaseOrderDTO>(d).slice(0, 5));
      }

      // 9 — recent invoices
      if (results[9].status === 'fulfilled') {
        const d = (results[9].value.data as any)?.data;
        setRecentInvoices(extractList<SalesInvoiceListDTO>(d).slice(0, 5));
      }

      setLastUpdated(new Date());
    } catch {
      setError(t('dashboard.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Greeting ───────────────────────────────────────────────────────────────
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t('dashboard.goodMorning');
    if (h < 17) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  })();

  const displayName = user
    ? (`${user.firstName ?? ''} ${user.lastName ?? ''}`).trim() || user.email
    : '';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="db-page">

        {/* ── Welcome header ─────────────────────────────────────────────── */}
        <div className="db-welcome">
          <div className="db-welcome-text">
            <h1 className="db-welcome-title">
              {greeting}{displayName ? `, ${displayName}` : ''} 👋
            </h1>
            <p className="db-welcome-sub">{t('dashboard.subtitle')}</p>
          </div>
          <div className="db-welcome-actions">
            {lastUpdated && (
              <span className="db-last-updated">
                {t('dashboard.lastUpdated')}: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              className="db-refresh-btn"
              onClick={loadData}
              disabled={loading}
              aria-label={t('dashboard.refresh')}
            >
              <span className={loading ? 'db-spin' : ''}>⟳</span> {t('dashboard.refresh')}
            </button>
          </div>
        </div>

        {/* ── Error banner ────────────────────────────────────────────────── */}
        {error && (
          <div className="ims-alert ims-alert-error db-error">{error}</div>
        )}

        {/* ── First-run setup checklist (auto-hides once complete) ────────── */}
        <SetupChecklist />

        {/* ── Primary stats row ───────────────────────────────────────────── */}
        <div className="db-stats-row">
          <StatCard
            icon={<Package size={24} strokeWidth={1.75} />} iconClass="db-icon-blue"
            value={totalProducts}
            label={t('dashboard.totalProducts')}
            onClick={() => navigate('/products')}
          />
          <StatCard
            icon={<AlertTriangle size={24} strokeWidth={1.75} />} iconClass="db-icon-amber"
            value={lowStockCount}
            label={t('dashboard.lowStockItems')}
            onClick={() => navigate('/stock/levels')}
          />
          <StatCard
            icon={<Warehouse size={24} strokeWidth={1.75} />} iconClass="db-icon-indigo"
            value={totalWarehouses}
            label={t('dashboard.activeWarehouses')}
            onClick={() => navigate('/warehouses')}
          />
          <StatCard
            icon={<Clock size={24} strokeWidth={1.75} />} iconClass="db-icon-red"
            value={pendingPOs}
            label={t('dashboard.pendingOrders')}
            onClick={() => navigate('/purchase-orders/approvals')}
          />
          <StatCard
            icon={<PackageCheck size={24} strokeWidth={1.75} />} iconClass="db-icon-green"
            value={grnStats?.totalGRN ?? null}
            label={t('dashboard.grnActivity')}
            onClick={() => navigate('/grn')}
          />
          <StatCard
            icon={<ArrowLeftRight size={24} strokeWidth={1.75} />} iconClass="db-icon-cyan"
            value={transferStats?.totalTransfers ?? null}
            label={t('dashboard.stockTransfers')}
            onClick={() => navigate('/stock-transfers')}
          />
          <StatCard
            icon={<PackageX size={24} strokeWidth={1.75} />} iconClass="db-icon-orange"
            value={damageStats?.totalDamageNotes ?? null}
            label={t('dashboard.damageNotes')}
            onClick={() => navigate('/damage-notes')}
          />
          <StatCard
            icon={<ScanLine size={24} strokeWidth={1.75} />} iconClass="db-icon-purple"
            value={reconcStats?.totalReconciliations ?? null}
            label={t('dashboard.reconciliation')}
            onClick={() => navigate('/stock-reconciliations')}
          />
        </div>

        {/* ── Main grid ──────────────────────────────────────────────────── */}
        <div className="db-main-grid">

          {/* Low stock alerts */}
          <div className="db-card db-card--wide">
            <div className="db-card-header">
              <span className="db-card-title"><AlertTriangle size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('dashboard.lowStockAlerts')}</span>
              <button className="db-view-all" onClick={() => navigate('/stock/levels')}>
                {t('dashboard.viewAll')}
              </button>
            </div>
            <div className="db-card-body">
              {lowStockItems.length === 0 && !loading ? (
                <p className="db-empty">{t('dashboard.noLowStockItems')}</p>
              ) : (
                <div className="db-table-wrapper">
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>{t('dashboard.product')}</th>
                        <th>{t('dashboard.warehouse')}</th>
                        <th className="db-text-right">{t('dashboard.available')}</th>
                        <th className="db-text-right">{t('dashboard.reserved')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStockItems.map((s: any) => (
                        <tr key={s.productId ?? s.id}>
                          <td>
                            <span className="db-product-name">{s.productName ?? s.product?.name ?? `#${s.productId}`}</span>
                            {(s.sku ?? s.product?.sku) && <span className="db-sku">{s.sku ?? s.product?.sku}</span>}
                          </td>
                          <td>{s.byWarehouse?.[0]?.warehouseName ?? s.warehouse?.name ?? '—'}</td>
                          <td className="db-text-right db-qty-low">{fmt.format(s.availableStock ?? s.availableQuantity ?? 0)}</td>
                          <td className="db-text-right db-qty-reserved">{fmt.format(s.reservedStock ?? s.reservedQuantity ?? 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Recent purchase orders */}
          <div className="db-card db-card--wide">
            <div className="db-card-header">
              <span className="db-card-title"><ShoppingCart size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('dashboard.recentPurchaseOrders')}</span>
              <button className="db-view-all" onClick={() => navigate('/purchase-orders')}>
                {t('dashboard.viewAll')}
              </button>
            </div>
            <div className="db-card-body">
              {recentPOs.length === 0 && !loading ? (
                <p className="db-empty">{t('dashboard.noPOs')}</p>
              ) : (
                <div className="db-table-wrapper">
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>{t('dashboard.poNumber')}</th>
                        <th>{t('dashboard.supplier')}</th>
                        <th>{t('dashboard.status')}</th>
                        <th className="db-text-right">{t('dashboard.amount')}</th>
                        <th>{t('dashboard.date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentPOs.map((po) => (
                        <tr key={po.id} className="db-row-link" onClick={() => navigate(`/purchase-orders/${po.id}`)}>
                          <td className="db-ref">{po.poNumber}</td>
                          <td>{po.supplierName ?? '—'}</td>
                          <td><POStatusBadge status={po.status} /></td>
                          <td className="db-text-right">{fmtCurrency.format(po.totalAmount)}</td>
                          <td>{formatDate(po.orderDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Recent sales invoices */}
          <div className="db-card db-card--wide">
            <div className="db-card-header">
              <span className="db-card-title"><Receipt size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('dashboard.recentInvoices')}</span>
              <button className="db-view-all" onClick={() => navigate('/sales-invoices')}>
                {t('dashboard.viewAll')}
              </button>
            </div>
            <div className="db-card-body">
              {recentInvoices.length === 0 && !loading ? (
                <p className="db-empty">{t('dashboard.noInvoices')}</p>
              ) : (
                <div className="db-table-wrapper">
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>{t('dashboard.invoiceNumber')}</th>
                        <th>{t('dashboard.customer')}</th>
                        <th>{t('dashboard.status')}</th>
                        <th className="db-text-right">{t('dashboard.amount')}</th>
                        <th className="db-text-right">{t('dashboard.balanceDue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentInvoices.map((inv) => (
                        <tr key={inv.id} className="db-row-link" onClick={() => navigate(`/sales-invoices/${inv.id}`)}>
                          <td className="db-ref">{inv.invoiceNumber}</td>
                          <td>{inv.customerName}</td>
                          <td><InvoiceStatusBadge status={inv.status} /></td>
                          <td className="db-text-right">{fmtCurrency.format(inv.totalAmount)}</td>
                          <td className="db-text-right db-balance">{fmtCurrency.format(inv.balanceDue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Workflow overview ───────────────────────────────────────────── */}
        <div className="db-section-title">{t('dashboard.workflowOverview')}</div>
        <div className="db-workflow-grid">
          <WorkflowCard
            title={<><PackageCheck size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('dashboard.grnActivity')}</>}
            onNavigate={() => navigate('/grn')}
            viewAllLabel={t('dashboard.viewAll')}
            rows={[
              { label: t('dashboard.total'),    value: grnStats?.totalGRN            ?? null },
              { label: t('dashboard.pending'),  value: grnStats?.pendingApproval     ?? null, accent: true },
              { label: t('dashboard.received'), value: grnStats?.totalItemsReceived  ?? null },
              { label: t('dashboard.defective'),value: grnStats?.totalDefectiveItems ?? null },
            ]}
          />
          <WorkflowCard
            title={<><ArrowLeftRight size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('dashboard.stockTransfers')}</>}
            onNavigate={() => navigate('/stock-transfers')}
            viewAllLabel={t('dashboard.viewAll')}
            rows={[
              { label: t('dashboard.total'),    value: transferStats?.totalTransfers  ?? null },
              { label: t('dashboard.pending'),  value: transferStats?.pendingApproval ?? null, accent: true },
              { label: t('dashboard.approved'), value: transferStats?.approved        ?? null },
              { label: t('dashboard.completed'),value: transferStats?.completed       ?? null },
            ]}
          />
          <WorkflowCard
            title={<><PackageX size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('dashboard.damageNotes')}</>}
            onNavigate={() => navigate('/damage-notes')}
            viewAllLabel={t('dashboard.viewAll')}
            rows={[
              { label: t('dashboard.total'),     value: damageStats?.totalDamageNotes   ?? null },
              { label: t('dashboard.pending'),   value: damageStats?.pendingApproval    ?? null, accent: true },
              { label: t('dashboard.posted'),    value: damageStats?.posted             ?? null },
              { label: t('dashboard.estimated'), value: damageStats?.totalEstimatedLoss ?? null },
            ]}
          />
          <WorkflowCard
            title={<><ScanLine size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('dashboard.reconciliation')}</>}
            onNavigate={() => navigate('/stock-reconciliations')}
            viewAllLabel={t('dashboard.viewAll')}
            rows={[
              { label: t('dashboard.total'),       value: reconcStats?.totalReconciliations ?? null },
              { label: t('dashboard.pending'),     value: reconcStats?.pendingApproval      ?? null, accent: true },
              { label: t('dashboard.withVariance'),value: reconcStats?.withVariance         ?? null },
              { label: t('dashboard.posted'),      value: reconcStats?.posted               ?? null },
            ]}
          />
        </div>

        {/* ── Quick actions ──────────────────────────────────────────────── */}
        <div className="db-section-title">{t('dashboard.quickActions')}</div>
        <div className="db-qa-grid">
          {(
            [
              { icon: <ShoppingCart size={28} strokeWidth={1.5} />, label: t('dashboard.qa_purchaseOrders'), path: '/purchase-orders/create' },
              { icon: <PackageCheck size={28} strokeWidth={1.5} />, label: t('dashboard.qa_grn'),            path: '/grn/create'             },
              { icon: <ArrowLeftRight size={28} strokeWidth={1.5} />, label: t('dashboard.qa_stockTransfer'),  path: '/stock-transfers/create' },
              { icon: <ScanLine size={28} strokeWidth={1.5} />,     label: t('dashboard.qa_reconciliation'), path: '/stock-reconciliations/create' },
              { icon: <PackageX size={28} strokeWidth={1.5} />,     label: t('dashboard.qa_damageNote'),     path: '/damage-notes/create'    },
              { icon: <Package size={28} strokeWidth={1.5} />,      label: t('dashboard.qa_stockLevels'),    path: '/stock/levels'           },
              { icon: <Receipt size={28} strokeWidth={1.5} />,      label: t('dashboard.qa_salesInvoice'),   path: '/sales-invoices'         },
              { icon: <Tag size={28} strokeWidth={1.5} />,          label: t('dashboard.qa_products'),       path: '/products'               },
            ] as { icon: React.ReactNode; label: string; path: string }[]
          ).map((qa) => (
            <button key={qa.path} className="db-qa-btn" onClick={() => navigate(qa.path)}>
              <span className="db-qa-icon">{qa.icon}</span>
              <span className="db-qa-label">{qa.label}</span>
            </button>
          ))}
        </div>

      </div>
    </Layout>
  );
};

export default DashboardPage;
